import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getStripeEnvironment } from "@/lib/stripe";

export type SubStatus = "inactive" | "trialing" | "active" | "past_due" | "canceled" | "comped";

export interface SubscriberRow {
  user_id: string;
  status: SubStatus;
  current_period_end: string | null;
  trial_started_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  comped_by: string | null;
  comped_reason: string | null;
}

export function useSubscription() {
  const { user } = useAuth();
  const [row, setRow] = useState<SubscriberRow | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRow = useCallback(async () => {
    if (!user) {
      setRow(null);
      setLoading(false);
      return;
    }
    const env = getStripeEnvironment();
    const { data } = await supabase
      .from("subscribers" as any)
      .select("*")
      .eq("user_id", user.id)
      .eq("environment", env)
      .maybeSingle();
    setRow((data as any) ?? null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    setLoading(true);
    fetchRow();
  }, [fetchRow]);

  // Realtime: refresh when our row changes (e.g. webhook update)
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`subscribers:${user.id}:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscribers", filter: `user_id=eq.${user.id}` },
        () => fetchRow()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user, fetchRow]);

  const status: SubStatus = row?.status ?? "inactive";
  const periodEnd = row?.current_period_end ? new Date(row.current_period_end) : null;
  const withinPeriod = !periodEnd || periodEnd.getTime() > Date.now();
  const isSubscribed =
    (status === "active" || status === "trialing" || status === "comped") && withinPeriod;
  const isTrialing = status === "trialing" && withinPeriod;
  const isComped = status === "comped";
  const daysLeft = periodEnd
    ? Math.max(0, Math.ceil((periodEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const startTrial = useCallback(async () => {
    const { data, error } = await supabase.rpc("start_trial_if_eligible" as any, {
      _environment: getStripeEnvironment(),
    });
    if (!error && data) {
      setRow(data as any);
    }
    return { data, error };
  }, []);

  return {
    row,
    status,
    isSubscribed,
    isTrialing,
    isComped,
    daysLeft,
    loading,
    refresh: fetchRow,
    startTrial,
  };
}